import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { getFreshAccessToken } from "../chat/api";
import { Header } from "../../components/Header/Header";
import PdfViewer from "../chat/PdfViewer";

export default function CreatedProposal() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const pdf_key = queryParams.get("pdf_key");

  const [proposalUrl, setProposalUrl] = useState(null);
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [progress, setProgress] = useState(null);
  const [progressMessage, setProgressMessage] = useState(""); // ✅ WS messages

  const wsRef = useRef(null);
  const calledRef = useRef(false);

  // --- connect websocket for progress ---
  const connectWebSocket = async (pdfKey) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("WS already connected, skipping…");
      return;
    }

    try {
      const token = await getFreshAccessToken();
      const ws = new WebSocket(
        `wss://stagingbackend.docusensa.com/ws/upload-status/?token=${token}`
      );

      ws.onopen = () => console.log("Proposal WS connected");

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.pdf_key !== pdfKey) return;
          if (data.metadata?.scope !== "proposal_generation") return;

          if (data.type === "progress") {
            setProgress(data.progress);
            setProgressMessage(data.message || "Generating…");
          } else if (data.type === "completed") {
            setProgress(100);
            setProgressMessage(data.message || "Proposal generation completed");
            setTimeout(() => {
              setProgress(null);
              setProposalUrl(data.metadata?.file_url || null);
              setStats(data.metadata?.stats || null);
              setStatus("completed");
            }, 1200);
          }
        } catch (err) {
          console.error("Proposal WS parse error", err);
        }
      };

      ws.onclose = () => console.log("Proposal WS closed");
      wsRef.current = ws;
    } catch (err) {
      console.error("Failed to connect proposal WS", err);
    }
  };

  // --- download proposal ---
  const handleDownloadProposal = async () => {
    if (!proposalUrl) return;
    try {
      setDownloading(true);
      const res = await api.get(proposalUrl, { responseType: "blob" });

      let filename = "proposal.docx";
      const cd = res.headers?.["content-disposition"];
      if (cd) {
        const match = /filename\*?=(?:UTF-8''|")?([^;"']+)/i.exec(cd);
        if (match && match[1]) filename = decodeURIComponent(match[1].replace(/"/g, ""));
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  // --- kick off proposal generation ---
  useEffect(() => {
    const fetchProposal = async () => {
      if (calledRef.current) return;
      calledRef.current = true;

      try {
        const res = await api.post(`/api/v1/rfp/documents/${pdf_key}/generate-proposal/`);

        // ✅ CASE 1: Already completed proposal exists
        if (res?.data?.status === "completed" && res?.data?.proposal?.proposal_url) {
          setStatus("completed");
          setProposalUrl(res.data.proposal.proposal_url);
          setStats(res.data?.stats || null);
          setProgress(null);
          setProgressMessage("");
          return;
        }

        // ✅ CASE 2: New proposal needs to be generated
        const status = res?.data?.status || "pending";
        setStatus(status);
        setProposalUrl(res?.data?.file_url || null);
        setStats(res?.data?.stats || null);

        if (status !== "completed") {
          setProgress(0);
          setProgressMessage("Starting proposal generation…");
          await connectWebSocket(pdf_key);
        }
      } catch (e) {
        console.error("Failed to load proposal", e);
        setStatus("failed");
      }
    };

    if (pdf_key) fetchProposal();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [pdf_key]);

  const handleCreateNewProposal = () => {
    navigate("/Response_writer");
  };

  // --- MAIN LAYOUT ---
  return (
    <div className="full-container-chat">
      {/* ✅ Fullscreen loader overlay */}
      {progress !== null && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/30 backdrop-blur-[1.5px]">
          {/* Progress circle */}
          <div className="relative w-20 h-20 mb-4 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke="#E5E7EB"
                strokeWidth="6"
                fill="transparent"
              />
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke="url(#gradient)"
                strokeWidth="6"
                fill="transparent"
                strokeDasharray={2 * Math.PI * 34}
                strokeDashoffset={2 * Math.PI * 34 * (1 - progress / 100)}
                strokeLinecap="round"
                className="transition-all duration-500 ease-in-out"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8B55DD" />
                  <stop offset="100%" stopColor="#BB42AE" />
                </linearGradient>
              </defs>
            </svg>
            {/* Percentage inside circle */}
            <span className="absolute text-white font-semibold text-lg">
              {progress}%
            </span>
          </div>

          {/* Main header */}
         
          {/* Real-time WS message */}
          {progressMessage && (
            <p className="text-lg font-semibold text-white mb-1">{progressMessage}</p>
          )}
        </div>
      )}

      {/* Normal page content */}
      <div className="-ml-16">
        <Header
          icon="/arrow-back.svg"
          name="Go to Proposals"
          onGoToContracts={() => navigate("/Response_writer")}
        />
      </div>

      <div className="flex gap-6">
        {/* Left Panel */}
        <div className="w-[35%] mt-10 flex-shrink-0 flex flex-col gap-6 item-center justify-center">
          {stats && (
            <div className="bg-white rounded-[12px] border p-5 shadow-sm ">
              <h3 className="font-semibold text-[16px] mb-3">Proposal Stats</h3>
              <div className="space-y-2 text-[14px] text-gray-700">
                <p>
                  Total Sections:{" "}
                  <span className="font-medium">{stats.total_sections}</span>
                </p>
                <p>
                  Answered Asks:{" "}
                  <span className="font-medium">{stats.answered_asks}</span>
                </p>
                <p>
                  Word Count:{" "}
                  <span className="font-medium">{stats.word_count}</span>
                </p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-[12px] border p-5 shadow-sm">
            <h3 className="font-semibold text-[16px] mb-3">Actions</h3>
            <div className="flex flex-col gap-3">
              {status === "pending" ? (
                <button className="flex items-center justify-center gap-2 px-4 py-2 rounded-[6px] bg-gray-300 text-gray-700 font-medium">
                  <img src="/loading-spinner.svg" className="w-4 h-4" alt="Loading" />
                  Generating Proposal...
                </button>
              ) : (
                <>
                  <button
                    onClick={handleDownloadProposal}
                    disabled={downloading || !proposalUrl}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-[6px] bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  >
                    📥 {downloading ? "Downloading…" : "Download Proposal"}
                  </button>
                  <button
                    onClick={handleCreateNewProposal}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-[6px] border border-gray-200 bg-gray-50 font-medium hover:bg-gray-100 transition"
                  >
                    ✨ Create New Proposal
                  </button>
                </>
              )}
            </div>
          </div>

         
        </div>

        {/* Right Panel */}
        <div className="w-full h-[30vh]">
          {status === "pending" ? (
            <p className="text-gray-500">Loading proposal...</p>
          ) : proposalUrl ? (
            <div className="w-full rounded-[8px] border">
              <PdfViewer pdfUrl={proposalUrl} title="Proposal Preview" />
            </div>
          ) : (
            <p className="text-red-500"></p>
          )}
        </div>
      </div>
    </div>
  );
}
